import { createElement } from "react";

import {
  DollarSignIcon,
  WalletIcon,
  CreditCardIcon,
  ShoppingCartIcon,
  CircleDollarSignIcon,
  GlobeIcon,
  StoreIcon,
  TrendingUpIcon,
  BadgePercentIcon,
  ShoppingBagIcon,
  SmartphoneIcon,
  LaptopMinimalIcon,
  PencilRulerIcon,
  TruckIcon,
  WrenchIcon,
  CircleCheckIcon,
} from "lucide-react";

import { chartColor } from "@/core/components/ui/chart-colors";

import type { ConversionRateCardProps } from "./chart-conversion-rate";
import type { CustomerRatingsCardProps } from "./chart-customer-ratings";
import type { EarningInsightsCardProps } from "./chart-earning-insights";
import type { EarningReportCardProps } from "./chart-earning-report";
import type { FinanceCardProps } from "./chart-finance";
import type { PerformanceCardProps } from "./chart-performance";
import type { ProjectTimelineCardProps } from "./chart-project-timeline";
import type { SalesMetricsCardProps } from "./chart-sales-metrics";
import type { SalesPerformanceCardProps } from "./chart-sales-performance";
import type { ServicesBySalesCardProps } from "./chart-services-by-sales";
import type { ChartTotalEarningCardProps } from "./chart-total-earning";
import type { TotalIncomeCardProps } from "./chart-total-income";
import type { TotalRevenueCardProps } from "./chart-total-revenue";
import type { TotalSalesCardProps } from "./chart-total-sales";
import type { TotalTransactionCardProps } from "./chart-total-transaction";
import type { TotalVisitorsCardProps } from "./chart-total-visitors";
import type { VehicleOverviewCardProps } from "./chart-vehicle-overview";
import type { WeeklyOverviewCardProps } from "./chart-weekly-overview";

export const demoMenuItems = ["Share", "Update", "Refresh"];

export const conversionRateDemoProps: ConversionRateCardProps = {
  title: "Conversion Rate",
  subTitle: "Compared to last month",
  totalConversion: 8.4,
  conversionTrend: "up",
  percentageChange: 4.8,
  conversionData: [
    {
      title: "Regular Use",
      stat: "12.4k sessions",
      trend: "up",
      percentageChange: 12.8,
    },
    {
      title: "Premium Plan",
      stat: "8.2k sessions",
      trend: "down",
      percentageChange: 3.1,
    },
  ],
  chartData: [
    { month: "January", conversion: 12 },
    { month: "February", conversion: 18 },
    { month: "March", conversion: 14 },
    { month: "April", conversion: 22 },
    { month: "May", conversion: 19 },
    { month: "June", conversion: 28 },
    { month: "July", conversion: 24 },
  ],
  conversionLabel: "Conversion",
  menuItems: demoMenuItems,
};

export const customerRatingsDemoProps: CustomerRatingsCardProps = {
  title: "Customer Ratings",
  rating: 4.5,
  pointsChange: "5.0",
  pointsChangeLabel: "Points from last month",
  onlineLabel: "Online",
  offlineLabel: "Offline",
  chartData: [
    { month: "January", online: 0.5, offline: 1 },
    { month: "February", online: 2, offline: 3.5 },
    { month: "March", online: 1, offline: 1.5 },
    { month: "April", online: 4.5, offline: 4 },
    { month: "May", online: 3, offline: 0.5 },
    { month: "June", online: 1.5, offline: 1 },
    { month: "July", online: 5, offline: 0.5 },
  ],
  menuItems: demoMenuItems,
};

export const earningInsightsDemoProps: EarningInsightsCardProps = {
  title: "Earning Insights",
  subTitle: "Weekly earnings overview",
  totalEarning: "$4,679",
  trend: "up",
  changePercentage: 28.14,
  description: "Total earnings compared to last week",
  chartData: [
    { day: "Monday", earning: 171 },
    { day: "Tuesday", earning: 201 },
    { day: "Wednesday", earning: 158 },
    { day: "Thursday", earning: 219 },
    { day: "Friday", earning: 190 },
    { day: "Saturday", earning: 232 },
    { day: "Sunday", earning: 181 },
  ],
  statData: [
    {
      icon: createElement(DollarSignIcon, { className: "size-4" }),
      title: "Earnings",
      amount: "$545.69",
      progress: 65,
    },
    {
      icon: createElement(WalletIcon, { className: "size-4" }),
      title: "Profit",
      amount: "$256.34",
      progress: 45,
    },
    {
      icon: createElement(CreditCardIcon, { className: "size-4" }),
      title: "Expense",
      amount: "$74.19",
      progress: 25,
    },
  ],
  earningLabel: "Earning",
  menuItems: demoMenuItems,
};

export const earningReportDemoProps: EarningReportCardProps = {
  title: "Earning Report",
  subTitle: "Weekly earnings overview",
  statData: [
    {
      icon: createElement(DollarSignIcon, { className: "size-5" }),
      title: "Net Profit",
      department: "Sales",
      value: "$1,619",
      trend: "up",
      percentage: 18.6,
      iconClassName: "bg-accent/10 text-accent-dark",
    },
    {
      icon: createElement(WalletIcon, { className: "size-5" }),
      title: "Total Income",
      department: "Store",
      value: "$3,571",
      trend: "up",
      percentage: 39.6,
      iconClassName: "bg-brand-500/10 text-brand-500",
    },
    {
      icon: createElement(CreditCardIcon, { className: "size-5" }),
      title: "Total Expenses",
      department: "Operations",
      value: "$430",
      trend: "down",
      percentage: 52.8,
      iconClassName: "bg-warning/10 text-warning",
    },
  ],
  chartData: [
    { day: "Monday", earning: 171 },
    { day: "Tuesday", earning: 201 },
    { day: "Wednesday", earning: 158 },
    { day: "Thursday", earning: 219 },
    { day: "Friday", earning: 190 },
    { day: "Saturday", earning: 232 },
    { day: "Sunday", earning: 181 },
  ],
  earningLabel: "Earning",
  menuItems: demoMenuItems,
};

export const financeDemoProps: FinanceCardProps = {
  title: "Finance",
  subTitle: "Yearly report overview",
  reportTitle: "Report",
  reportSubTitle: "Monthly Avg. $45.578k",
  labels: { profit: "Profit", income: "Income", expense: "Expense" },
  chartData: [
    { month: "January", profit: 20, income: 0, expense: 0 },
    { month: "February", profit: 20, income: 8, expense: 0 },
    { month: "March", profit: 18, income: 22, expense: 0 },
    { month: "April", profit: 12, income: 13, expense: 0 },
    { month: "May", profit: 22, income: 18, expense: 4 },
    { month: "June", profit: 15, income: 22, expense: 13 },
    { month: "July", profit: 25, income: 7, expense: 12 },
  ],
  reportData: [
    {
      icon: createElement(DollarSignIcon, { className: "size-[18px]" }),
      title: "Total Profit",
      amount: "$48,568.20",
      iconClassName: "bg-accent/10 text-accent-dark",
    },
    {
      icon: createElement(WalletIcon, { className: "size-[18px]" }),
      title: "Total Income",
      amount: "$38,453.25",
      iconClassName: "bg-brand-500/10 text-brand-500",
    },
    {
      icon: createElement(CreditCardIcon, { className: "size-[18px]" }),
      title: "Total Expense",
      amount: "$2,453.45",
      iconClassName: "bg-warning/10 text-warning",
    },
  ],
  actionLabel: "View Report",
  menuItems: demoMenuItems,
};

export const performanceDemoProps: PerformanceCardProps = {
  title: "Performance",
  tabLabels: {
    newUsers: "New Users",
    onlineSales: "Online Sales",
    dailySales: "Daily Sales",
  },
  newUsers: {
    user: {
      name: "Angel George",
      role: "Product Manager",
      avatarFallback: "AG",
    },
    badgeLabel: "Daily purchase",
    itemsCount: "10 Items",
    productLabel: "Physical product",
    productValue: "$78,263",
    trendPercentage: "14.78%",
    avatars: [
      { fallback: "OS", name: "Olivia Sparks" },
      { fallback: "HL", name: "Howard Lloyd" },
      { fallback: "HR", name: "Hallie Richards" },
      { fallback: "JW", name: "Jenny Wilson" },
    ],
    viewAllLabel: "View all",
    footerValue: "Increase 24%",
    footerText: "More email marketing to reach your acquisition target.",
  },
  onlineSales: {
    first: { label: "Digital product", value: "7,589", trend: "up" },
    second: { label: "Physical product", value: "8,365", trend: "down" },
    highlightLabel: "Physical product",
    highlightValue: "$78,263",
    highlightChange: "5.6%",
    salesLabel: "Sales",
    chartData: [
      { period: "Jan", sales: 280 },
      { period: "Feb", sales: 400 },
      { period: "Mar", sales: 280 },
      { period: "Apr", sales: 590 },
      { period: "May", sales: 360 },
      { period: "Jun", sales: 460 },
      { period: "Jul", sales: 400 },
    ],
    footerValue: "18%",
    footerText: "until your target this month",
  },
  dailySales: {
    first: { label: "Digital product", value: "8,365", trend: "up" },
    second: { label: "Physical product", value: "7,589", trend: "down" },
    highlightLabel: "Average daily sales",
    highlightValue: "$8,263",
    highlightChange: "3.4%",
    salesLabel: "Sales",
    chartData: [
      { period: "Monday", sales: 120 },
      { period: "Tuesday", sales: 240 },
      { period: "Wednesday", sales: 190 },
      { period: "Thursday", sales: 270 },
      { period: "Friday", sales: 210 },
      { period: "Saturday", sales: 320 },
      { period: "Sunday", sales: 270 },
    ],
    footerValue: "12%",
    footerText: "until your daily purchase target",
  },
  menuItems: demoMenuItems,
};

export const projectTimelineDemoProps: ProjectTimelineCardProps = {
  title: "Project Timeline",
  subTitle: "Total 840 Task Completed",
  listTitle: "Project List",
  listSubTitle: "4 ongoing project",
  timelineLabel: "Timeline",
  timelineData: [
    {
      name: "Caleb",
      startDate: "2025-01-01",
      endDate: "2025-05-15",
      project: "UI Design",
      fill: chartColor("accent"),
    },
    {
      name: "Shaw",
      startDate: "2025-03-10",
      endDate: "2025-06-15",
      project: "UX Design",
      fill: chartColor("info"),
    },
    {
      name: "Jane",
      startDate: "2025-02-25",
      endDate: "2025-06-17",
      project: "Music",
      fill: chartColor("success"),
    },
    {
      name: "Blake",
      startDate: "2025-01-25",
      endDate: "2025-07-03",
      project: "Animation",
      fill: chartColor("primary"),
    },
    {
      name: "Quinn",
      startDate: "2025-03-03",
      endDate: "2025-07-26",
      project: "Prototyping",
      fill: chartColor("brand"),
    },
  ],
  projectList: [
    {
      icon: createElement(SmartphoneIcon),
      projectTitle: "IOS Application",
      tasks: "Task 840/2.5K",
      avatarClassName: "bg-accent/10 text-accent-dark",
    },
    {
      icon: createElement(LaptopMinimalIcon),
      projectTitle: "Web Application",
      tasks: "Task 99/1.42K",
      avatarClassName: "bg-info/10 text-info",
    },
    {
      icon: createElement(CreditCardIcon),
      projectTitle: "Brand Dashboard",
      tasks: "Task 58/100",
      avatarClassName: "bg-success/10 text-success",
    },
    {
      icon: createElement(PencilRulerIcon),
      projectTitle: "UI Kit Design",
      tasks: "Task 120/350",
      avatarClassName: "bg-brand-500/10 text-brand-500",
    },
  ],
  xAxisTicks: [
    { value: 0, label: "Jan" },
    { value: 30, label: "Feb" },
    { value: 60, label: "Mar" },
    { value: 90, label: "Apr" },
    { value: 120, label: "May" },
    { value: 150, label: "Jun" },
    { value: 180, label: "Jul" },
    { value: 210, label: "Aug" },
  ],
  menuItems: demoMenuItems,
};

export const salesMetricsDemoProps: SalesMetricsCardProps = {
  title: "Sales metrics",
  companyName: "Sandy' Company",
  companyEmail: "sandy@company.com",
  metrics: [
    {
      icon: createElement(TrendingUpIcon, { className: "size-5" }),
      title: "Sales trend",
      value: "$11,548",
    },
    {
      icon: createElement(BadgePercentIcon, { className: "size-5" }),
      title: "Discount offers",
      value: "$1,326",
    },
    {
      icon: createElement(DollarSignIcon, { className: "size-5" }),
      title: "Net profit",
      value: "$17,356",
    },
    {
      icon: createElement(ShoppingBagIcon, { className: "size-5" }),
      title: "Total orders",
      value: "248",
    },
  ],
  revenueGoal: {
    title: "Revenue goal",
    chartData: [
      { month: "january", sales: 340, fill: "var(--color-january)" },
      { month: "february", sales: 200, fill: "var(--color-february)" },
      { month: "march", sales: 200, fill: "var(--color-march)" },
    ],
    centerValue: "256.24",
    centerLabel: "Total Profit",
    planCompletedLabel: "Plan completed",
    planCompletedValue: "56%",
  },
  salesPlan: {
    title: "Sales plan",
    percentage: 54,
    description: "Percentage profit from total sales",
    cohortTitle: "Cohort analysis indicators",
    cohortDescription:
      "Analyzes the behaviour of a group of users who joined a product/service at the same time over a certain period.",
    openStatisticsLabel: "Open Statistics",
    percentageChangeLabel: "Percentage Change",
  },
  revenueSeriesLabels: {
    january: "January",
    february: "February",
    march: "March",
  },
  salesLabel: "Sales",
};

export const salesPerformanceDemoProps: SalesPerformanceCardProps = {
  title: "Sales performance",
  value: "$2,150.00",
  changePercentage: 5,
  badgeLabels: {
    excellent: "Excellent",
    good: "Good",
    average: "Average",
    bad: "Bad",
    poor: "Poor",
  },
  salesData: [
    { title: "Online store", value: 85, color: "bg-accent" },
    { title: "Offline store", value: 55, color: "bg-brand-500" },
  ],
  detailsLabel: "Details",
};

export const servicesBySalesDemoProps: ServicesBySalesCardProps = {
  title: "Top Services by Sales",
  salesLabel: "Sales",
  salesData: [
    { service: "UI design", sales: 99, fill: chartColor("brand") },
    { service: "UX design", sales: 94, fill: chartColor("accent") },
    { service: "Music", sales: 80, fill: chartColor("success") },
    { service: "Animation", sales: 68, fill: chartColor("warning") },
    { service: "React", sales: 52, fill: chartColor("info") },
    { service: "SEO", sales: 45, fill: chartColor("primary") },
  ],
  menuItems: demoMenuItems,
};

export const chartTotalEarningDemoProps: ChartTotalEarningCardProps = {
  title: "Total earning",
  percentage: 87,
  trend: "up",
  changePercentage: 38,
  currentLabel: "2025",
  previousLabel: "2024",
  chartData: [
    { name: "January", previous: -2120, current: 2080 },
    { name: "February", previous: -1720, current: 1560 },
    { name: "March", previous: -2841, current: 2320 },
    { name: "April", previous: -1720, current: 2080 },
    { name: "May", previous: -2200, current: 1160 },
    { name: "June", previous: -2200, current: 2480 },
    { name: "July", previous: -1200, current: 2682 },
    { name: "August", previous: -2200, current: 960 },
  ],
  items: [
    {
      icon: createElement(DollarSignIcon, { className: "size-5" }),
      title: "Total revenue",
      status: "Successful payments",
      value: "+$250",
    },
    {
      icon: createElement(ShoppingCartIcon, { className: "size-5" }),
      title: "Total sales",
      status: "Refund",
      value: "+$80",
    },
  ],
  menuItems: demoMenuItems,
};

export const totalIncomeDemoProps: TotalIncomeCardProps = {
  title: "Total Income",
  subTitle: "Weekly report overview",
  reportTitle: "Report",
  reportSubTitle: "Weekly activity",
  incomesLabel: "Incomes",
  chartData: [
    { day: "Monday", incomes: 3100 },
    { day: "Tuesday", incomes: 3100 },
    { day: "Wednesday", incomes: 5000 },
    { day: "Thursday", incomes: 5000 },
    { day: "Friday", incomes: 4000 },
    { day: "Saturday", incomes: 4000 },
    { day: "Sunday", incomes: 5920 },
  ],
  reportData: [
    {
      icon: createElement(WalletIcon, {
        className: "size-6 stroke-[1.5] text-accent-dark",
      }),
      title: "Income",
      amount: "$5,550",
    },
    {
      icon: createElement(CreditCardIcon, {
        className: "size-6 stroke-[1.5] text-brand-500",
      }),
      title: "Expense",
      amount: "$3,520",
    },
    {
      icon: createElement(CircleDollarSignIcon, {
        className: "size-6 stroke-[1.5] text-info",
      }),
      title: "Profit",
      amount: "$2,350",
    },
  ],
  menuItems: demoMenuItems,
};

const growthChartDemoData = [
  1, 1, 0.9, 0.9, 0.8, 0.8, 0.7, 0.7, 0.6, 0.6, 0.5, 0.5, 0.4, 0.4, 0.3, 0.3,
  0.2, 0.2, 0.1, 0.1, 0.05, 0.05,
].map((alpha, index) => ({
  date: `2023-12-${String(index + 1).padStart(2, "0")}`,
  revenue: 20,
  fill: alpha === 1 ? chartColor("primary") : chartColor("primary", alpha),
}));

export const totalRevenueDemoProps: TotalRevenueCardProps = {
  title: "Total Revenue",
  currentLabel: "2024",
  previousLabel: "2023",
  revenueLabel: "Revenue",
  chartData: [
    { name: "January", previous: -13, current: 21 },
    { name: "February", previous: -16, current: 10 },
    { name: "March", previous: -14, current: 13 },
    { name: "April", previous: -10, current: 12 },
    { name: "May", previous: -17, current: 20 },
    { name: "June", previous: -13, current: 12 },
    { name: "July", previous: -12, current: 15 },
  ],
  selectItems: [
    { label: "Revenue", value: "revenue" },
    { label: "Expenses", value: "expenses" },
    { label: "Profit", value: "profit" },
    { label: "Loss", value: "loss" },
    { label: "Net Income", value: "net_income" },
  ],
  selectPlaceholder: "Report",
  growth: {
    percentage: "78%",
    label: "Growth",
    caption: "62% Company Growth",
    chartData: growthChartDemoData,
  },
  summaryData: [
    {
      icon: createElement(CircleDollarSignIcon, { className: "size-5" }),
      period: "2024",
      amount: "$32.5K",
    },
    {
      icon: createElement(WalletIcon, { className: "size-5" }),
      period: "2023",
      amount: "$41.2K",
    },
  ],
  menuItems: demoMenuItems,
};

export const totalSalesDemoProps: TotalSalesCardProps = {
  title: "Total sales",
  value: "$2,150.00",
  changePercentage: 5,
  onlineLabel: "Online Store",
  offlineLabel: "Offline Store",
  chartData: [
    { time: "09:00", online: 88, offline: 88 },
    { time: "10:00", online: 88, offline: 88 },
    { time: "11:00", online: 144, offline: 144 },
    { time: "12:00", online: 144, offline: 144 },
    { time: "13:00", online: 109, offline: 109 },
    { time: "14:00", online: 102, offline: 109 },
    { time: "15:00", online: 62, offline: 62 },
    { time: "16:00", online: 62, offline: 62 },
    { time: "17:00", online: 128, offline: 144 },
    { time: "18:00", online: 144, offline: 144 },
    { time: "19:00", online: 183, offline: 200 },
    { time: "20:00", online: 200, offline: 200 },
  ],
  platforms: [
    {
      icon: createElement(GlobeIcon, { className: "size-4" }),
      platform: "Online Store",
      sales: "$20k",
      growth: "+12.6%",
    },
    {
      icon: createElement(StoreIcon, { className: "size-4" }),
      platform: "Offline Store",
      sales: "$20k",
      growth: "-4.2%",
    },
  ],
  detailsLabel: "Details",
};

export const totalTransactionDemoProps: TotalTransactionCardProps = {
  title: "Total Transaction",
  subTitle: "Weekly overview",
  reportTitle: "Report",
  reportSubTitle: "Last month transactions $23.4K",
  transactionLabel: "Transaction",
  chartData: [
    { month: "January", transaction: 38000 },
    { month: "February", transaction: 52000 },
    { month: "March", transaction: 32000 },
    { month: "April", transaction: 12000 },
    { month: "May", transaction: 35000 },
    { month: "June", transaction: 28000 },
    { month: "July", transaction: 33000 },
    { month: "August", transaction: 25000 },
  ],
  stats: [
    {
      icon: createElement(CircleDollarSignIcon, { className: "size-6" }),
      label: "This week",
      value: "+82.46%",
      iconClassName: "bg-info/10 text-info",
    },
    {
      icon: createElement(WalletIcon, { className: "size-6" }),
      label: "This week",
      value: "-24.8%",
      iconClassName: "bg-accent/10 text-accent-dark",
    },
  ],
  performanceLabel: "Performance",
  performanceValue: "+94.13%",
  actionLabel: "View Report",
  menuItems: demoMenuItems,
};

export const totalVisitorsDemoProps: TotalVisitorsCardProps = {
  title: "Total visitors",
  totalVisitors: "12,4k",
  percentage: 12,
  visitorData: [
    {
      product: "Online store",
      percentage: 62,
      amount: "7,689",
      trend: "up",
      heightClass: "h-28",
      color: "bg-accent",
    },
    {
      product: "Offline store",
      percentage: 38,
      amount: "4,711",
      trend: "down",
      heightClass: "h-16",
      color: "bg-brand-500",
    },
  ],
  detailsLabel: "Details",
};

export const vehicleOverviewDemoProps: VehicleOverviewCardProps = {
  title: "Vehicle Overview",
  vehicleData: [
    {
      status: "On the way",
      percentage: 42,
      time: "2h 45min",
      icon: createElement(TruckIcon),
      width: "w-[42%]",
      colors: "bg-brand-500 text-primary-foreground",
    },
    {
      status: "Unloading",
      percentage: 28,
      time: "1h 20min",
      icon: createElement(WrenchIcon),
      width: "w-[28%]",
      colors: "bg-accent text-accent-foreground",
    },
    {
      status: "Loading",
      percentage: 17,
      time: "55min",
      icon: createElement(CircleDollarSignIcon),
      width: "w-[17%]",
      colors: "bg-info text-info-foreground",
    },
    {
      status: "Idle",
      percentage: 13,
      time: "3h 10min",
      icon: createElement(CircleCheckIcon),
      width: "w-[13%]",
      colors: "bg-muted text-muted-foreground",
    },
  ],
  menuItems: demoMenuItems,
};

export const weeklyOverviewDemoProps: WeeklyOverviewCardProps = {
  title: "Weekly overview",
  salesLabel: "Sales",
  profitLabel: "Profit",
  performanceValue: "80%",
  performanceText: "Your sales performance is 60% better compared to last month",
  detailsLabel: "Details",
  chartData: [
    { name: "Page A", sales: 46400, profit: 35000 },
    { name: "Page B", sales: 69230, profit: 62000 },
    { name: "Page C", sales: 49845, profit: 41000 },
    { name: "Page D", sales: 90000, profit: 80000 },
    { name: "Page E", sales: 83100, profit: 64000 },
    { name: "Page F", sales: 74770, profit: 67000 },
    { name: "Page G", sales: 47770, profit: 40000 },
    { name: "Page H", sales: 30000, profit: 24000 },
  ],
  menuItems: demoMenuItems,
};
